import { useCallback, useRef, useState } from 'react';
import type { FilePond as FilePondInstance } from 'react-filepond';

import { centerCropToBlob } from './center-crop';

export function useCropQueue({
  cropAspectRatio,
  pondRef,
  onUploaded,
}: {
  cropAspectRatio: number;
  pondRef: React.RefObject<FilePondInstance | null>;
  onUploaded?: (key: string, filename: string) => void;
}) {
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState('');

  const pendingFileIdRef = useRef<string | null>(null);
  const pendingFileNameRef = useRef<string>('cropped.webp');
  const pendingOriginalFileRef = useRef<File | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const croppedFileRef = useRef<File | null>(null);
  const cropQueueRef = useRef<Array<{ file: File; fileId: string }>>([]);
  const cropDialogActiveRef = useRef(false);
  const reportedServerIdsRef = useRef<Set<string>>(new Set());

  const openCropDialog = useCallback((file: File, fileId: string) => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    pendingFileIdRef.current = fileId;
    pendingFileNameRef.current = file.name;
    pendingOriginalFileRef.current = file;
    cropDialogActiveRef.current = true;
    setCropSrc(url);
    setCropOpen(true);
  }, []);

  const processNextInQueue = useCallback(() => {
    cropDialogActiveRef.current = false;
    const next = cropQueueRef.current.shift();
    if (next) {
      openCropDialog(next.file, next.fileId);
    }
  }, [openCropDialog]);

  const handleDialogExited = useCallback(() => {
    if (cropDialogActiveRef.current) {
      return;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setCropSrc('');
  }, []);

  const handleCropConfirm = useCallback(
    (blob: Blob) => {
      const pond = pondRef.current;
      const fileId = pendingFileIdRef.current;
      if (!pond || !fileId) {
        return;
      }

      const croppedFile = new File([blob], pendingFileNameRef.current.replace(/\.[^.]+$/, '.webp'), {
        type: 'image/webp',
      });
      croppedFileRef.current = croppedFile;
      pond.removeFile(fileId);
      pond.addFile(croppedFile).then(pondFile => {
        pond.processFile(pondFile.id);
        processNextInQueue();
      });
      setCropOpen(false);
    },
    [pondRef, processNextInQueue],
  );

  const handleCropSkip = useCallback(async () => {
    const fileId = pendingFileIdRef.current;
    const pond = pondRef.current;
    setCropOpen(false);

    if (!fileId || !pond) {
      processNextInQueue();
      return;
    }

    const originalFile = pendingOriginalFileRef.current;
    if (!isNaN(cropAspectRatio) && originalFile) {
      try {
        const blob = await centerCropToBlob(originalFile, cropAspectRatio);
        const croppedFile = new File([blob], pendingFileNameRef.current.replace(/\.[^.]+$/, '.webp'), {
          type: 'image/webp',
        });
        croppedFileRef.current = croppedFile;
        pond.removeFile(fileId);
        pond.addFile(croppedFile).then(pondFile => {
          pond.processFile(pondFile.id);
          processNextInQueue();
        });
        return;
      } catch {
        // fall through to upload original
      }
    }

    pond.processFile(fileId);
    processNextInQueue();
  }, [cropAspectRatio, pondRef, processNextInQueue]);

  const handleCropCancel = useCallback(() => {
    const fileId = pendingFileIdRef.current;
    if (fileId) {
      pondRef.current?.removeFile(fileId);
    }
    setCropOpen(false);
    processNextInQueue();
  }, [pondRef, processNextInQueue]);

  const handleProcessFile = useCallback(
    (_error: unknown, file: { serverId: unknown; filename: string }) => {
      const key = file.serverId as string;
      if (!key) {
        return;
      }
      if (reportedServerIdsRef.current.has(key)) {
        return;
      }
      reportedServerIdsRef.current.add(key);
      onUploaded?.(key, file.filename);
    },
    [onUploaded],
  );

  const onAddFile = useCallback(
    (file: File, fileId: string) => {
      if (croppedFileRef.current && file === croppedFileRef.current) {
        croppedFileRef.current = null;
        return;
      }
      if (cropDialogActiveRef.current) {
        cropQueueRef.current.push({ file, fileId });
        return;
      }
      openCropDialog(file, fileId);
    },
    [openCropDialog],
  );

  const onRemoveFile = useCallback((serverId: string | null, fileId: string) => {
    if (serverId) {
      reportedServerIdsRef.current.delete(serverId);
    }
    cropQueueRef.current = cropQueueRef.current.filter(q => q.fileId !== fileId);
  }, []);

  return {
    cropOpen,
    cropSrc,
    croppedFileRef,
    handleCropConfirm,
    handleCropSkip,
    handleCropCancel,
    handleDialogExited,
    handleProcessFile,
    onAddFile,
    onRemoveFile,
  };
}
